var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/assert-exists.ts
var NotFoundError = class extends Error {
  static {
    __name(this, "NotFoundError");
  }
  constructor(message) {
    super(message);
    this.name = "NotFoundError";
  }
};
async function assertExists(read, id, label) {
  const result = await read(id);
  if (!result) {
    throw new NotFoundError(`${label} with id ${id} not found`);
  }
}
__name(assertExists, "assertExists");

// src/toast-types.ts
var TOAST_DEFAULTS = {
  duration: 4e3,
  maxWeb: 3,
  maxMobile: 1,
  swipeThreshold: 80
};
var TOAST_SEVERITY_MAP = {
  success: "success",
  error: "danger",
  warning: "warning",
  info: "info"
};

// src/use-toast-timer.ts
import { useState, useEffect, useRef, useCallback } from "react";
function useToastTimer(params) {
  const { id, duration, onDismiss } = params;
  const [progress, setProgress] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [remainingMs, setRemainingMs] = useState(duration);
  const startTimeRef = useRef(Date.now());
  const elapsedBeforePauseRef = useRef(0);
  const rafRef = useRef(void 0);
  const durationRef = useRef(duration);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [
    onDismiss
  ]);
  useEffect(() => {
    durationRef.current = duration;
  }, [
    duration
  ]);
  const tick = useCallback(() => {
    if (durationRef.current === Infinity) {
      return;
    }
    const totalElapsed = elapsedBeforePauseRef.current + (Date.now() - startTimeRef.current);
    const p = Math.max(0, 1 - totalElapsed / durationRef.current);
    const remaining = Math.max(0, durationRef.current - totalElapsed);
    setProgress(p);
    setRemainingMs(remaining);
    if (p <= 0) {
      onDismissRef.current(id);
      return;
    }
    rafRef.current = requestAnimationFrame((time) => tick());
  }, [
    id
  ]);
  useEffect(() => {
    if (duration === Infinity) {
      setProgress(1);
      setRemainingMs(Infinity);
      return;
    }
    startTimeRef.current = Date.now();
    elapsedBeforePauseRef.current = 0;
    setProgress(1);
    setRemainingMs(duration);
    rafRef.current = requestAnimationFrame((time) => tick());
    return () => {
      if (rafRef.current !== void 0) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    id,
    duration,
    tick
  ]);
  const pause = useCallback(() => {
    if (isPaused || durationRef.current === Infinity) {
      return;
    }
    setIsPaused(true);
    if (rafRef.current !== void 0) {
      cancelAnimationFrame(rafRef.current);
    }
    elapsedBeforePauseRef.current += Date.now() - startTimeRef.current;
  }, [
    isPaused
  ]);
  const resume = useCallback(() => {
    if (!isPaused || durationRef.current === Infinity) {
      return;
    }
    setIsPaused(false);
    startTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame((time) => tick());
  }, [
    isPaused,
    tick
  ]);
  return {
    progress,
    isPaused,
    pause,
    resume,
    remainingMs
  };
}
__name(useToastTimer, "useToastTimer");

// src/index.ts
var ConflictError = class extends Error {
  static {
    __name(this, "ConflictError");
  }
  constructor(message) {
    super(message);
    this.name = "ConflictError";
  }
};
function assertTransition(input) {
  const { entity, current, next, map } = input;
  const allowed = map[current];
  if (!allowed || !allowed.includes(next)) {
    throw new ConflictError(`Illegal ${entity} transition: ${current} -> ${next}`);
  }
  return next;
}
__name(assertTransition, "assertTransition");
function createStatusTransition(entity, map) {
  return (current, next) => assertTransition({
    entity,
    current,
    next,
    map
  });
}
__name(createStatusTransition, "createStatusTransition");

export {
  __name,
  NotFoundError,
  assertExists,
  TOAST_DEFAULTS,
  TOAST_SEVERITY_MAP,
  useToastTimer,
  ConflictError,
  assertTransition,
  createStatusTransition
};
//# sourceMappingURL=chunk-2JD2Q6DL.js.map